/**
 * AudioEngine — Tone.js music + SFX with graceful autoplay handling, plus
 * haptics. Synthesized (no asset downloads), so it ships tiny and works
 * offline. Volumes and mute persist to localStorage.
 *
 * Tone is imported lazily on first use so the game boots instantly and only
 * pays the audio cost after a user gesture (required by browser autoplay).
 */

type ToneModule = typeof import('tone');

export type SfxName =
  | 'click'
  | 'confirm'
  | 'back'
  | 'hover'
  | 'coin'
  | 'powerup'
  | 'hit'
  | 'explode'
  | 'shoot'
  | 'levelup'
  | 'lose'
  | 'win'
  | 'place'
  | 'clear'
  | 'error';

export interface AudioMix {
  master: number;
  music: number;
  sfx: number;
  muted: boolean;
}

const STORE_KEY = 'arcade:audio-mix';

function loadMix(): AudioMix {
  try {
    const raw = localStorage.getItem(STORE_KEY);
    if (raw) return { master: 0.9, music: 0.6, sfx: 0.9, muted: false, ...JSON.parse(raw) };
  } catch {
    /* ignore */
  }
  return { master: 0.9, music: 0.6, sfx: 0.9, muted: false };
}

export class AudioEngine {
  private Tone: ToneModule | null = null;
  private ready = false;
  private starting: Promise<void> | null = null;
  private mix: AudioMix = loadMix();

  private masterGain: any = null;
  private musicGain: any = null;
  private sfxGain: any = null;

  private musicLoop: any = null;
  private sfxSynth: any = null;
  private noiseSynth: any = null;
  private currentTheme: string | null = null;

  get mixState(): AudioMix {
    return { ...this.mix };
  }

  /** Must be called from a user gesture. Idempotent. */
  async unlock(): Promise<void> {
    if (this.ready) return;
    if (this.starting) return this.starting;
    this.starting = (async () => {
      const Tone = (this.Tone ??= await import('tone'));
      await Tone.start();
      this.masterGain = new Tone.Gain(this.mix.muted ? 0 : this.mix.master).toDestination();
      this.musicGain = new Tone.Gain(this.mix.music).connect(this.masterGain);
      this.sfxGain = new Tone.Gain(this.mix.sfx).connect(this.masterGain);
      this.sfxSynth = new Tone.PolySynth(Tone.Synth, {
        envelope: { attack: 0.005, decay: 0.1, sustain: 0.1, release: 0.2 },
      }).connect(this.sfxGain);
      this.noiseSynth = new Tone.NoiseSynth({
        noise: { type: 'white' },
        envelope: { attack: 0.005, decay: 0.15, sustain: 0 },
      }).connect(this.sfxGain);
      this.ready = true;
    })();
    return this.starting;
  }

  private persist() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(this.mix));
    } catch {
      /* ignore */
    }
  }

  setMaster(v: number) {
    this.mix.master = clamp01(v);
    if (this.masterGain) this.masterGain.gain.rampTo(this.mix.muted ? 0 : this.mix.master, 0.05);
    this.persist();
  }
  setMusic(v: number) {
    this.mix.music = clamp01(v);
    if (this.musicGain) this.musicGain.gain.rampTo(this.mix.music, 0.05);
    this.persist();
  }
  setSfx(v: number) {
    this.mix.sfx = clamp01(v);
    if (this.sfxGain) this.sfxGain.gain.rampTo(this.mix.sfx, 0.05);
    this.persist();
  }
  setMuted(m: boolean) {
    this.mix.muted = m;
    if (this.masterGain) this.masterGain.gain.rampTo(m ? 0 : this.mix.master, 0.05);
    this.persist();
  }
  toggleMute() {
    this.setMuted(!this.mix.muted);
    return this.mix.muted;
  }

  /* -------------------------------- SFX ---------------------------------- */

  private static SFX: Record<SfxName, { notes: string[]; dur: string; noise?: boolean; vel?: number }> = {
    click: { notes: ['C6'], dur: '32n', vel: 0.4 },
    hover: { notes: ['G5'], dur: '64n', vel: 0.2 },
    confirm: { notes: ['C5', 'G5', 'C6'], dur: '16n' },
    back: { notes: ['G5', 'C5'], dur: '16n', vel: 0.5 },
    coin: { notes: ['E6', 'A6'], dur: '32n', vel: 0.5 },
    powerup: { notes: ['C5', 'E5', 'G5', 'C6'], dur: '16n' },
    hit: { notes: ['A2'], dur: '16n', noise: true, vel: 0.6 },
    explode: { notes: ['C2'], dur: '8n', noise: true, vel: 0.9 },
    shoot: { notes: ['C4'], dur: '32n', vel: 0.35 },
    levelup: { notes: ['C5', 'E5', 'G5', 'C6', 'E6'], dur: '16n' },
    lose: { notes: ['C4', 'A3', 'F3', 'C3'], dur: '8n' },
    win: { notes: ['C5', 'G5', 'C6', 'E6', 'G6'], dur: '8n' },
    place: { notes: ['C3'], dur: '32n', vel: 0.4 },
    clear: { notes: ['C5', 'G5', 'C6'], dur: '16n' },
    error: { notes: ['C3', 'B2'], dur: '16n', vel: 0.6 },
  };

  play(name: SfxName) {
    if (!this.ready || this.mix.muted) return;
    const Tone = this.Tone!;
    const spec = AudioEngine.SFX[name];
    if (!spec) return;
    if (spec.noise) {
      this.noiseSynth.triggerAttackRelease(spec.dur, undefined, spec.vel ?? 0.7);
      return;
    }
    const now = Tone.now();
    spec.notes.forEach((n, i) => {
      this.sfxSynth.triggerAttackRelease(n, spec.dur, now + i * 0.06, spec.vel ?? 0.6);
    });
  }

  /* ------------------------------- music --------------------------------- */

  /**
   * Start a generative ambient/arpeggio theme. `theme` picks a chord palette;
   * `seed` varies the sequence deterministically per level.
   */
  startMusic(theme: 'ambient' | 'arcade' | 'tense' | 'zen' | 'triumph' = 'ambient', seed = 1) {
    if (!this.ready) return;
    if (this.currentTheme === `${theme}:${seed}`) return;
    this.stopMusic();
    const Tone = this.Tone!;
    const palettes: Record<string, string[]> = {
      ambient: ['C3', 'E3', 'G3', 'A3', 'C4', 'E4', 'G4', 'B4'],
      arcade: ['C3', 'D3', 'F3', 'G3', 'A3', 'C4', 'D4', 'F4'],
      tense: ['A2', 'C3', 'E3', 'F3', 'A3', 'C4', 'E4', 'F4'],
      zen: ['D3', 'F3', 'A3', 'C4', 'D4', 'F4', 'A4', 'C5'],
      triumph: ['C3', 'G3', 'C4', 'E4', 'G4', 'C5', 'E5', 'G5'],
    };
    const scale = palettes[theme] || palettes.ambient;
    const synth = new Tone.PolySynth(Tone.FMSynth, {
      harmonicity: 1.5,
      modulationIndex: 3,
      envelope: { attack: 0.4, decay: 0.3, sustain: 0.5, release: 2 },
      volume: -12,
    }).connect(this.musicGain);
    const reverb = new Tone.Reverb({ decay: 4, wet: 0.35 }).connect(this.musicGain);
    synth.connect(reverb);

    let step = 0;
    let rng = seed >>> 0 || 1;
    const rand = () => ((rng = (rng * 1664525 + 1013904223) >>> 0) / 0xffffffff);
    this.musicLoop = new Tone.Loop((time: number) => {
      const note = scale[Math.floor(rand() * scale.length)];
      synth.triggerAttackRelease(note, '2n', time, 0.5 + rand() * 0.3);
      if (step % 4 === 0) {
        const bass = scale[Math.floor(rand() * 3)];
        synth.triggerAttackRelease(bass, '1n', time, 0.4);
      }
      step++;
    }, '2n');
    Tone.Transport.bpm.value = theme === 'zen' ? 60 : theme === 'tense' ? 132 : 96;
    this.musicLoop.start(0);
    Tone.Transport.start();
    this.currentTheme = `${theme}:${seed}`;
  }

  stopMusic() {
    if (this.musicLoop) {
      this.musicLoop.stop();
      this.musicLoop.dispose();
      this.musicLoop = null;
    }
    this.currentTheme = null;
    try {
      this.Tone?.Transport.stop();
    } catch {
      /* ignore */
    }
  }
}

/* -------------------------------- haptics -------------------------------- */

export const Haptics = {
  enabled: true,
  can(): boolean {
    return this.enabled && typeof navigator !== 'undefined' && 'vibrate' in navigator;
  },
  buzz(pattern: number | number[]) {
    if (this.can()) {
      try {
        navigator.vibrate(pattern);
      } catch {
        /* ignore */
      }
    }
  },
  light() {
    this.buzz(10);
  },
  medium() {
    this.buzz(25);
  },
  heavy() {
    this.buzz([40]);
  },
  success() {
    this.buzz([15, 40, 25]);
  },
  warn() {
    this.buzz([30, 30, 30]);
  },
  hit() {
    this.buzz([12, 20, 30]);
  },
};

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

/** Convenience singleton (games may also construct their own). */
export const audio = new AudioEngine();
